// Project supporting-photo section (Session AP). The Projects module had no photo attachment
// outside the quotation form, so this is the smallest addition: attach reference images of the
// lifts / components in scope, view them, delete them, and run "Identify this part" on any of
// them. It reuses the shared JobPhotoGallery (with the project photo proxy) and the shared
// PartIdentify renderer, so there is one photo grid and one identification UI across the platform.
// It is deliberately NOT part of the quotation form or the quotation PDF.
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { JobPhotoGallery } from "./JobPhotoGallery";
import { Loader } from "../../components/Loader";
import { prepareImagesForUpload } from "../../lib/imageUpload";

export function ProjectPhotos({ projectId }: { projectId: string }) {
  const [count, setCount] = useState(0);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.projects.photoCount(projectId);
      setCount(r.count);
    } catch {
      setError("Could not load the photos.");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = await prepareImagesForUpload(Array.from(e.target.files ?? []));
    if (files.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.projects.uploadPhotos(projectId, files);
      setCount(r.count);
      setVersion((v) => v + 1);
    } catch (err) {
      setError(friendlyApiError(err, "Could not upload the photo. Please try again."));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deletePhoto = async (index: number) => {
    setError("");
    try {
      const r = await api.projects.deletePhoto(projectId, index);
      setCount(r.count);
      setVersion((v) => v + 1);
    } catch (err) {
      setError(friendlyApiError(err, "Could not delete the photo. Please try again."));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-base uppercase tracking-wide text-ele-strong">Photos</h3>
          <p className="mt-1 max-w-xl text-xs text-ele-body opacity-70">
            Reference images of the lifts or components in scope, kept as supporting material (not part of the quotation). Use "Identify" on a
            photo for an AI best-guess of the part.
          </p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onPick}
            disabled={busy}
            id="project-photo-input"
            className="hidden"
          />
          <label
            htmlFor="project-photo-input"
            className={`inline-flex cursor-pointer items-center gap-2 rounded-ele border border-ele-green px-4 py-2 font-menu text-xs tracking-wide text-ele-green transition hover:bg-ele-inset ${busy ? "pointer-events-none opacity-50" : ""}`}
          >
            {busy ? "Uploading..." : "Add photos"}
          </label>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-ele-danger">
          {error}
        </p>
      )}

      {loading ? (
        <Loader shape="inline" className="mt-3" />
      ) : (
        <JobPhotoGallery
          jobId={projectId}
          count={count}
          label=""
          emptyText="No photos attached yet."
          version={version}
          buildPhotoUrl={(i) => api.projects.photoUrl(projectId, i)}
          altLabel={(i) => `Project photo ${i + 1}`}
          onDeletePhoto={deletePhoto}
          identify={{ source: "project", id: projectId }}
        />
      )}
    </div>
  );
}
